/**
 * Activity Helper Utilities
 * Functions for managing, duplicating, validating, and exporting activities
 */

/**
 * Generate a unique activity ID
 */
export const generateActivityId = () => {
  return `activity-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Duplicate an activity with a new ID and modified title
 */
export const duplicateActivity = (activity, existingActivities = []) => {
  if (!activity) return null;

  // Find existing copies to determine the new copy number
  const baseName = activity.title.replace(/ \(Copy(?: \d+)?\)$/, '');
  const existingCopies = existingActivities.filter((a) =>
    a.title.startsWith(baseName) && a.title.match(/ \(Copy(?: \d+)?\)$/)
  );

  let copyNumber = 1;
  if (existingCopies.length > 0) {
    // Extract copy numbers and find the highest
    const copyNumbers = existingCopies
      .map((a) => {
        const match = a.title.match(/ \(Copy(?: (\d+))?\)$/);
        return match ? (match[1] ? parseInt(match[1]) : 1) : 0;
      })
      .filter((n) => n > 0);

    if (copyNumbers.length > 0) {
      copyNumber = Math.max(...copyNumbers) + 1;
    }
  }

  // Create duplicated activity
  const duplicatedActivity = {
    ...JSON.parse(JSON.stringify(activity)), // Deep clone
    id: generateActivityId(),
    title: copyNumber === 1 ? `${baseName} (Copy)` : `${baseName} (Copy ${copyNumber})`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    status: 'pending',
    responses: [], // Clear responses from original
  };

  // Remove original's MongoDB _id so backend creates a new one
  delete duplicatedActivity._id;

  return duplicatedActivity;
};

/**
 * Validate an activity has all required fields
 */
export const validateActivity = (activity) => {
  const errors = [];

  // Check required fields
  if (!activity.title || activity.title.trim() === '') {
    errors.push('Activity title is required');
  }

  if (!activity.type) {
    errors.push('Activity type is required');
  }

  // Type-specific validation
  switch (activity.type) {
    case 'poll':
      if (!activity.config?.question) {
        errors.push('Poll question is required');
      }
      if (!activity.config?.options || activity.config.options.length < 2) {
        errors.push('Poll must have at least 2 options');
      }
      break;

    case 'quiz':
      if (!activity.config?.question) {
        errors.push('Quiz question is required');
      }
      if (!activity.config?.options || activity.config.options.length < 2) {
        errors.push('Quiz must have at least 2 options');
      }
      // Check if at least one correct answer exists
      const hasCorrectAnswer = activity.config?.options?.some((opt) => opt.isCorrect);
      if (!hasCorrectAnswer) {
        errors.push('Quiz must have at least one correct answer');
      }
      break;

    case 'word_cloud':
      if (!activity.config?.question) {
        errors.push('Word cloud question is required');
      }
      break;

    case 'open_question':
      if (!activity.config?.question) {
        errors.push('Question text is required');
      }
      break;

    case 'qna':
      // Q&A doesn't require specific config
      break;

    case 'feedback':
      if (!activity.config?.question) {
        errors.push('Feedback question is required');
      }
      break;

    case 'break':
      if (!activity.duration || activity.duration <= 0) {
        errors.push('Break duration is required');
      }
      break;

    default:
      errors.push(`Unknown activity type: ${activity.type}`);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Export activities to JSON format
 */
export const exportActivitiesToJSON = (activities, sessionInfo = {}) => {
  const exportData = {
    version: '1.0',
    exportedAt: new Date().toISOString(),
    sessionInfo: {
      name: sessionInfo.name || 'Exported Activities',
      description: sessionInfo.description || '',
      ...sessionInfo,
    },
    activities: activities.map((activity) => ({
      ...activity,
      exportedFrom: 'Training Platform',
    })),
  };

  const jsonString = JSON.stringify(exportData, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `activities-${Date.now()}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  return exportData;
};

/**
 * Import activities from JSON file
 */
export const importActivitiesFromJSON = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);

        // Validate import format
        if (!data.activities || !Array.isArray(data.activities)) {
          reject(new Error('Invalid activity import format'));
          return;
        }

        // Assign new IDs to imported activities to avoid conflicts
        const importedActivities = data.activities.map((activity) => ({
          ...activity,
          id: generateActivityId(),
          importedAt: new Date().toISOString(),
        }));

        resolve({
          activities: importedActivities,
          sessionInfo: data.sessionInfo || {},
          metadata: {
            version: data.version,
            exportedAt: data.exportedAt,
          },
        });
      } catch (error) {
        reject(new Error(`Failed to parse JSON file: ${error.message}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsText(file);
  });
};

/**
 * Parse CSV and convert to activities
 */
export const parseCSVToActivities = (csvText) => {
  const lines = csvText.trim().split('\n');

  if (lines.length < 2) {
    throw new Error('CSV file must have at least a header row and one data row');
  }

  // Parse header
  const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());

  // Required headers
  const requiredHeaders = ['title', 'type'];
  const missingHeaders = requiredHeaders.filter((h) => !headers.includes(h));

  if (missingHeaders.length > 0) {
    throw new Error(`Missing required CSV headers: ${missingHeaders.join(', ')}`);
  }

  // Parse activities
  const activities = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    // Simple CSV parsing (doesn't handle quoted commas)
    const values = line.split(',').map((v) => v.trim());
    const activity = {
      id: generateActivityId(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // Map values to headers
    headers.forEach((header, index) => {
      const value = values[index] || '';

      switch (header) {
        case 'title':
          activity.title = value;
          break;

        case 'type':
          activity.type = value.toLowerCase().replace(/\s+/g, '_');
          break;

        case 'description':
          activity.description = value;
          break;

        case 'duration':
        case 'timelimit':
          activity.timeLimit = parseInt(value) || 0;
          break;

        case 'required':
          activity.required = value.toLowerCase() === 'true' || value === '1';
          break;

        case 'tags':
          activity.tags = value
            .split(';')
            .map((t) => t.trim())
            .filter((t) => t);
          break;

        case 'question':
          if (!activity.config) activity.config = {};
          activity.config.question = value;
          break;

        case 'options':
          if (!activity.config) activity.config = {};
          activity.config.options = value
            .split(';')
            .map((o) => o.trim())
            .filter((o) => o);
          break;

        case 'points':
          if (!activity.config) activity.config = {};
          activity.config.points = parseInt(value) || 0;
          break;

        default:
          // Store unknown fields in metadata
          if (!activity.metadata) activity.metadata = {};
          activity.metadata[header] = value;
      }
    });

    // Set default config if not present
    if (!activity.config) {
      activity.config = {};
    }

    activities.push(activity);
  }

  return activities;
};

/**
 * Import activities from CSV file
 */
export const importActivitiesFromCSV = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const csvText = event.target.result;
        const activities = parseCSVToActivities(csvText);

        resolve({
          activities,
          importedFrom: file.name,
          importedAt: new Date().toISOString(),
        });
      } catch (error) {
        reject(new Error(`Failed to parse CSV file: ${error.message}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsText(file);
  });
};

/**
 * Export activities to CSV format
 */
export const exportActivitiesToCSV = (activities) => {
  if (!activities || activities.length === 0) {
    throw new Error('No activities to export');
  }

  // CSV headers
  const headers = [
    'Title',
    'Type',
    'Description',
    'Duration',
    'Required',
    'Tags',
    'Question',
    'Options',
    'Points',
  ];

  // Build CSV rows
  const rows = [headers.join(',')];

  activities.forEach((activity) => {
    const row = [
      `"${activity.title || ''}"`,
      activity.type || '',
      `"${activity.description || ''}"`,
      activity.timeLimit || '',
      activity.required ? 'true' : 'false',
      activity.tags ? activity.tags.join(';') : '',
      `"${activity.config?.question || ''}"`,
      activity.config?.options
        ? Array.isArray(activity.config.options)
          ? activity.config.options
              .map((o) => (typeof o === 'string' ? o : o.text))
              .join(';')
          : ''
        : '',
      activity.config?.points || '',
    ];

    rows.push(row.join(','));
  });

  const csvContent = rows.join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `activities-${Date.now()}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  return csvContent;
};

/**
 * Get activity type info (icon, color, label)
 */
export const getActivityTypeInfo = (type) => {
  const typeMap = {
    poll: {
      icon: '📊',
      label: 'Poll',
      color: 'bg-blue-100 text-blue-700 border-blue-300',
      description: 'Collect opinions and votes',
    },
    quiz: {
      icon: '📝',
      label: 'Quiz',
      color: 'bg-green-100 text-green-700 border-green-300',
      description: 'Test knowledge with graded questions',
    },
    word_cloud: {
      icon: '☁️',
      label: 'Word Cloud',
      color: 'bg-purple-100 text-purple-700 border-purple-300',
      description: 'Collect and visualize words',
    },
    open_question: {
      icon: '❓',
      label: 'Open Question',
      color: 'bg-amber-100 text-amber-700 border-amber-300',
      description: 'Collect detailed text responses',
    },
    qna: {
      icon: '🙋',
      label: 'Q&A',
      color: 'bg-pink-100 text-pink-700 border-pink-300',
      description: 'Interactive question and answer session',
    },
    feedback: {
      icon: '⭐',
      label: 'Feedback',
      color: 'bg-indigo-100 text-indigo-700 border-indigo-300',
      description: 'Collect ratings and feedback',
    },
    break: {
      icon: '☕',
      label: 'Break',
      color: 'bg-neutral-100 text-neutral-700 border-neutral-300',
      description: 'Scheduled break time',
    },
  };

  return (
    typeMap[type] || {
      icon: '📋',
      label: 'Unknown',
      color: 'bg-neutral-100 text-neutral-700 border-neutral-300',
      description: 'Unknown activity type',
    }
  );
};

/**
 * Calculate total session duration from activities
 */
export const calculateSessionDuration = (activities) => {
  if (!activities || activities.length === 0) return 0;

  return activities.reduce((total, activity) => {
    // Check multiple possible locations for timeLimit
    const timeLimit = activity.timeLimit || activity.duration || activity.config?.timeLimit || 0;
    // Convert seconds to minutes if timeLimit is in seconds (> 60 likely means seconds)
    const minutes = timeLimit > 60 ? Math.round(timeLimit / 60) : timeLimit;
    return total + minutes;
  }, 0);
};

/**
 * Get activity statistics
 */
export const getActivityStats = (activities) => {
  if (!activities || activities.length === 0) {
    return {
      total: 0,
      byType: {},
      totalDuration: 0,
      requiredCount: 0,
      optionalCount: 0,
    };
  }

  const stats = {
    total: activities.length,
    byType: {},
    totalDuration: calculateSessionDuration(activities),
    requiredCount: activities.filter((a) => a.required || a.config?.required).length,
    optionalCount: activities.filter((a) => !a.required && !a.config?.required).length,
  };

  // Count by type
  activities.forEach((activity) => {
    const type = activity.type || 'unknown';
    stats.byType[type] = (stats.byType[type] || 0) + 1;
  });

  return stats;
};
